import {
  ComposedChart as RechartsComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { axisLabel, axisMargin, legendStyle } from './chart-utils';
import { CategoryTick, categoryAxis } from './axis-ticks';

interface ComposedChartProps {
  data: Record<string, unknown>[];
  /** Gemeinsame Config fuer Bar- und Line-Serien (Keys entsprechen den Serien) */
  config: ChartConfig;
  xAxisKey: string;
  /** Keys, die als Balken gerendert werden */
  bars: string[];
  /** Keys, die als Linien gerendert werden */
  lines: string[];
  height?: number;
  /** Primary-Y-Achse Einheit */
  unit?: string;
  /** Secondary-Y-Achse Einheit (nur wenn dualAxis=true) */
  unitRight?: string;
  /** Achsentitel/Einheit der X-Achse (z.B. "Quartal"). */
  xUnit?: string;
  /** Achsentitel der linken Y-Achse. Default: `unit`. */
  yUnit?: string;
  /** Achsentitel der rechten Y-Achse (nur dualAxis). Default: `unitRight`. */
  yUnitRight?: string;
  /** Zweite Y-Achse rechts: Lines nutzen die rechte Achse, Bars die linke */
  dualAxis?: boolean;
  /** Bars gestapelt rendern (nur bei mehreren Bars sinnvoll) */
  stackedBars?: boolean;
  /** Linien kurven (monotone) statt gerade */
  curved?: boolean;
  /** Datenpunkte auf Linien anzeigen */
  dots?: boolean;
  /** Balken-Rundung oben */
  radius?: number;
  /** Maximale Balkenbreite in px (verhindert zu breite Balken bei wenigen Datenpunkten) */
  maxBarSize?: number;
  className?: string;
  ariaLabel?: string;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * ComposedChart - Kombiniert Bar- und Line-Diagramme in einem Chart.
 *
 * Typische Einsaetze:
 *   - Absolute Werte (Bars) + Trendlinie (Line)
 *   - Mengenangaben (Bars) + Prozentanteile (Line, optional rechte Achse)
 *   - Vergleich zweier Messreihen mit unterschiedlichen Groessenordnungen via dualAxis
 *
 * @example Bars + Trendlinie:
 * data={[{ monat: 'Jan', umsatz: 1200, trend: 1100 }, …]}
 * config={{
 *   umsatz: { label: 'Umsatz', color: '…' },
 *   trend:  { label: 'Trend',  color: '…' },
 * }}
 * bars={['umsatz']} lines={['trend']}
 *
 * @example Duale Achse (Bars absolut, Line in %):
 * data={[{ quartal: 'Q1', faelle: 420, zufriedenheit: 92 }]}
 * bars={['faelle']} lines={['zufriedenheit']}
 * dualAxis unitRight="%"
 */
export function CPComposedChart({
  data,
  config,
  xAxisKey,
  bars,
  lines,
  height = 350,
  unit,
  unitRight,
  xUnit,
  yUnit,
  yUnitRight,
  dualAxis = false,
  stackedBars = false,
  curved = true,
  dots = true,
  radius = 0,
  maxBarSize = 64,
  className,
  ariaLabel,
  decimals,
}: ComposedChartProps) {
  // Achsentitel: explizite *Unit-Props haben Vorrang, sonst die Tooltip-Einheiten.
  const leftLabel = yUnit ?? unit;
  const rightLabel = dualAxis ? (yUnitRight ?? unitRight) : undefined;

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      {(containerWidth) => {
        const { angled, height: categoryAxisHeight } = categoryAxis({ data, xAxisKey, containerWidth, xUnit });
        return (
      <RechartsComposedChart data={data} margin={axisMargin({ xUnit, yUnit: leftLabel, yUnitRight: rightLabel })}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-brand-border, #e5e7eb)" />
        <XAxis
          dataKey={xAxisKey}
          interval={0}
          tick={<CategoryTick angled={angled} />}
          height={categoryAxisHeight}
          tickLine={false}
          axisLine={false}
          label={axisLabel(xUnit, 'x')}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }}
          tickLine={false}
          axisLine={false}
          label={axisLabel(leftLabel, 'y')}
        />
        {dualAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }}
            tickLine={false}
            axisLine={false}
            label={axisLabel(rightLabel, 'yRight')}
          />
        )}
        <Tooltip
          content={
            <ChartTooltipContent
              config={config}
              unit={unit}
              unitRight={dualAxis ? unitRight : undefined}
              rightKeys={dualAxis ? lines : []}
              decimals={decimals}
            />
          }
        />
        {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
            nach series_key ('value'); null behält die Config-/Editor-Reihenfolge. */}
        <Legend
          itemSorter={null}
          iconType="circle"
          iconSize={8}
          wrapperStyle={legendStyle(xUnit)}
          formatter={(value: string) => config[value]?.label ?? value}
        />
        {bars.map((key) => (
          <Bar
            key={key}
            yAxisId="left"
            dataKey={key}
            stackId={stackedBars ? 'bar-stack' : undefined}
            fill={`var(--color-${key})`}
            radius={[radius, radius, 0, 0] as [number, number, number, number]}
            maxBarSize={maxBarSize}
          />
        ))}
        {lines.map((key) => (
          <Line
            key={key}
            yAxisId={dualAxis ? 'right' : 'left'}
            type={curved ? 'monotone' : 'linear'}
            dataKey={key}
            stroke={`var(--color-${key})`}
            strokeWidth={2}
            dot={dots ? { r: 3, fill: `var(--color-${key})` } : false}
            activeDot={{ r: 5 }}
          />
        ))}
      </RechartsComposedChart>
        );
      }}
    </ChartContainer>
  );
}
